import { z } from 'zod';
import { languageSchema } from "./languages.js";
/**
 * Subtitles exchanged directly between the two browsers.
 *
 * This is not signalling and deliberately does not go through the server. Each
 * side translates only the *other* person's voice, so each side holds half the
 * picture: my browser knows what you said and what it means in my language,
 * and yours knows the same about me. To show both speakers on one screen the
 * two halves have to meet, and the only place they can meet without the server
 * seeing speech is the peer connection itself — encrypted, browser to browser.
 *
 * Section 9 keeps transcripts out of the server and off disk. Sending them
 * over the signalling socket would have broken that; a data channel does not.
 */
/** One frame is a fragment of speech, not a novel. */
export const MAX_TRANSCRIPT_BYTES = 8 * 1024;
export const peerTranscriptSchema = z.object({
    type: z.literal('transcript'),
    /**
     * From the sender's point of view: `original` is what they heard me say,
     * `translation` is what they are hearing it as. The receiver flips the
     * labels, because the sender's input is the receiver's own voice.
     */
    kind: z.enum(['original', 'translation']),
    text: z.string().max(2000),
    language: languageSchema,
    /** Sender's session id, so text from a restarted session is not mixed in. */
    sessionId: z.string().max(64),
    /**
     * Set when this session reopened a translator that fell asleep in a silence.
     * The session id still changes, but nothing was cut mid-sentence, so the
     * receiver keeps the lines already on screen instead of clearing them.
     */
    resumed: z.boolean().optional(),
    at: z.number().int().positive(),
});
/**
 * What one side's providers have been used for so far in this call, so the
 * creator's screen can show what the whole conversation costs (task 6). Each
 * browser only knows its own sessions; this is the other half.
 *
 * Numbers only, like telemetry: there is nothing in it that could carry
 * speech. Cumulative, so a lost frame is corrected by the next one.
 */
export const peerUsageSchema = z.object({
    type: z.literal('usage'),
    /** Seconds a translation session was open. The provider bills by the open session. */
    translateSeconds: z.number().finite().min(0).max(86_400),
    /** Seconds the source language was transcribed as well, billed on top. */
    transcribeSeconds: z.number().finite().min(0).max(86_400),
    /** Characters sent to the second voice. */
    voiceChars: z.number().int().min(0).max(10_000_000),
});
/** Everything the channel between the two browsers may carry. */
export const peerMessageSchema = z.discriminatedUnion('type', [
    peerTranscriptSchema,
    peerUsageSchema,
]);
export const TRANSCRIPT_CHANNEL = 'voxa-transcript';
//# sourceMappingURL=transcript.js.map